import {
  createContext,
  type FC,
  lazy,
  type PropsWithChildren,
  Suspense,
  useCallback,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useTimezone } from '../../../useTimezone/useTimezone'

const TimeSchedulerModal = lazy(async () => {
  const { TimeSchedulerModal } = await import('../TimeSchedulerModal/TimeSchedulerModal')
  return { default: TimeSchedulerModal }
})

export const TimeSchedulerContext = createContext<() => Promise<string>>(() => {
  throw Error('Time scheduler not resolved')
})

const TimeSchedulerProvider: FC<PropsWithChildren> = ({ children }) => {
  const resolver = useRef<{
    resolve: (time: string) => void
    reject: () => void
  }>()
  const timezone = useTimezone()

  const [isOpen, setIsOpen] = useState(false)
  const [hasOpened, setHasOpened] = useState(false)

  const scheduleTime = useCallback(async (): Promise<string> => {
    setIsOpen(true)
    setHasOpened(true)

    return await new Promise<string>((resolve, reject) => {
      resolver.current = { resolve, reject }
    })
  }, [resolver])

  const handleTimeConfirmation = (date: string): void => {
    setIsOpen(false)
    resolver.current?.resolve(date)
  }

  const handleDecline = (): void => {
    setIsOpen(false)

    resolver.current?.reject()
  }

  const handleSchedulerFailure = (): void => {
    setHasOpened(false)
    handleDecline()
  }

  return (
    <TimeSchedulerContext.Provider value={scheduleTime}>
      {hasOpened && (
        <ErrorBoundary fallback={null} onError={handleSchedulerFailure}>
          <Suspense fallback={null}>
            <TimeSchedulerModal
              open={isOpen}
              onTimeConfirmed={handleTimeConfirmation}
              onDecline={handleDecline}
              onClose={handleDecline}
              timezone={timezone}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {children}
    </TimeSchedulerContext.Provider>
  )
}

export default TimeSchedulerProvider
